import flatMap from 'lodash/flatMap'
import sumBy from 'lodash/sumBy';
import * as React from 'react';
import { ItemType } from '../../classes/item-type';
import { PriceTypePrice } from '../../classes/level';
import { PriceType } from '../../classes/price-type';
import PriceTypeQuantitySelect from '../../common/selects/quantity-select';
import { getCurrencyFormat } from '../../helpers/currency-format';
import { Html } from '../../helpers/html-helpers';
import { sortPriceTypes } from '../../helpers/price-type-helpers';
import { formatString } from '../../helpers/string-format';
import { IAppSettingsModel } from '../../reducers/fetch-settings-reducer';
import GroupSeatingMessage from './group-seating-message';



export interface IPriceTypeFilterContainerProps {
    appSettingsModel: IAppSettingsModel;
    onChangePriceTypeQuantity: (priceTypeId: number, quantity: number) => void;
    priceTypes: PriceType[];
    multipleOfSeatsRequiredToContinue: number;
    showComponent: boolean;
}

class PriceTypeFilterContainer extends React.PureComponent<IPriceTypeFilterContainerProps, any>{
    useTagAlert = '<use xlink:href="#icon-alert" />';

    render() {
        if (!this.props.showComponent) return null;

        var hitMaxTicketLimit = sumBy(this.props.priceTypes, pt => pt.selectedQuantity) >= this.props.appSettingsModel.maxTicketQuantity;

        return (
            <section className="filters-section">
                <h2 className="heading-general">{this.props.appSettingsModel.numberOfTicketsTitle}</h2>
                <div className="filters-section-message">
                    {this.props.appSettingsModel.numberOfTicketsMessage &&
                        <p>{this.props.appSettingsModel.numberOfTicketsMessage.replace('{MaxQuantity}', this.props.appSettingsModel.maxTicketQuantity.toString())}</p>
                    }
                </div>
                {
                    this.props.appSettingsModel.groupTicketMessaging !== null && this.props.appSettingsModel.groupTicketMessaging.length > 0
                        ?
                        <GroupSeatingMessage
                            groupTicketMessaging={this.props.appSettingsModel.groupTicketMessaging}
                            alertComponent={hitMaxTicketLimit} />
                        : null
                }

                {
                    this.props.multipleOfSeatsRequiredToContinue > 0 ?
                        <div className={`group-messaging-section alert`}>
                            <svg className="ticket-selection-quantity-max-alert" dangerouslySetInnerHTML={{ __html: this.useTagAlert }} />
                            {Html.renderParagraph(this.props.appSettingsModel.forceMultipleFilterMessageText
                                .replace("{0}", this.props.multipleOfSeatsRequiredToContinue.toString()), "caption-paragraph")}
                        </div>
                        : null
                }

                <div className="seat-info-prices-type-list">
                    {this.getSortedPriceTypes().map(priceType =>
                        <PriceTypeQuantitySelect key={priceType.priceTypeId}
                            quantity={priceType.selectedQuantity}
                            onSelectQuantity={this.props.onChangePriceTypeQuantity}
                            displayText={priceType.title}
                            priceTypeId={priceType.priceTypeId}
                            messageText={priceType.message}
                            startingAtText={this.getStartingAtText(priceType)}
                            priceTypeMaxReached={(priceType.selectedQuantity >= this.getPriceTypeMaxQuantity(priceType)) || this.getSpecialSeatingUnlockQuantityReached(priceType)}
                            priceTypeMaxError={this.getPriceTypeMaxErrorMessage(priceType)}
                            canAdd={(!hitMaxTicketLimit && (priceType.selectedQuantity < this.getPriceTypeMaxQuantity(priceType))) && !this.getSpecialSeatingUnlockQuantityReached(priceType)}
                            canRemove={priceType.selectedQuantity !== 0 && priceType.selectedQuantity > this.getPriceTypeMinQuantity(priceType) && this.getAllowPriceRemoveBasedOnSpecialSeating(priceType)}
                            feeAmount={this.getPriceTypeFeeAmount(priceType)}
                            maxQuantity={this.getPriceTypeMaxQuantity(priceType)}
                        />
                    )}
                </div>

            </section>
        );
    }

    //_onSelectQuantity = (quantity, priceTypeId) => this.props.onChangePriceTypeQuantity(priceTypeId, quantity);

    getStartingAtText(priceType: PriceType): string {
        const priceText = getCurrencyFormat(this.props.appSettingsModel.currencyFormat, priceType.minTotalPrice);
        const message = formatString(
            this.props.appSettingsModel.itemType === ItemType.Fixed || this.props.appSettingsModel.itemType === ItemType.Flex
                || this.props.appSettingsModel.itemType === ItemType.Super
                ?
                this.props.appSettingsModel.subscriptionFilterPackageTicketStartingPrice
                : this.props.appSettingsModel.filterTicketStartingPrice, [priceText]);
        return message;
    }

    getSpecialSeatingUnlockQuantityReached(priceType: PriceType): boolean {
        if (priceType.attachedSeatingOptionGroup == null)
            return false;

        if (priceType.attachedSeatingOptionGroup.unlockSeatingOptionGroupId == null)
            return false;

        var matchededUnlockPriceType = this.props.priceTypes.filter(pt => pt.attachedSeatingOptionGroup != null
            && pt.attachedSeatingOptionGroup.id == priceType.attachedSeatingOptionGroup.unlockSeatingOptionGroupId);

        if (matchededUnlockPriceType.length == 0)
            return false;

        if (priceType.attachedSeatingOptionGroup.seatLimitPerUnlockSeat > 0) {
            return priceType.selectedQuantity >= matchededUnlockPriceType[0].selectedQuantity * priceType.attachedSeatingOptionGroup.seatLimitPerUnlockSeat;
        }

        // if there are no limits then just check if an unlock seat has been requested
        return matchededUnlockPriceType[0].selectedQuantity > 0;

    }

    getAllowPriceRemoveBasedOnSpecialSeating(priceType: PriceType): boolean {
        if (priceType.attachedSeatingOptionGroup == null) {
            return true;
        }
        var matchededUnlockPriceType = this.props.priceTypes.filter(pt => pt.attachedSeatingOptionGroup != null
            && pt.attachedSeatingOptionGroup.unlockSeatingOptionGroupId == priceType.attachedSeatingOptionGroup.id);

        if (matchededUnlockPriceType.length == 0)
            return true;

        if (matchededUnlockPriceType[0].attachedSeatingOptionGroup.seatLimitPerUnlockSeat > 0) {

            return (priceType.selectedQuantity - 1) * matchededUnlockPriceType[0].attachedSeatingOptionGroup.seatLimitPerUnlockSeat >= matchededUnlockPriceType[0].selectedQuantity;
        }

        // if there are no limits then you can remove whenever
        return true;

    }

    getPriceTypeMaxErrorMessage(priceType: PriceType) {
        if (this.getSpecialSeatingUnlockQuantityReached(priceType)) {
            // this means the seat has already been unlocked
            if (priceType.selectedQuantity > 0) {
                return this.props.appSettingsModel.maxQuantityPriceTypeSelectedError.replace("{0}", priceType.title);
            } else {
                return priceType.attachedSeatingOptionGroup.addSeatsToUnlockFilterMessage;
            }

        } else {
            return this.props.appSettingsModel.maxQuantityPriceTypeSelectedError.replace("{0}", priceType.title);
        }
    }

    getSortedPriceTypes() {
        return sortPriceTypes(this.props.priceTypes);
    }

    getPriceTypeMaxQuantity(priceType: PriceType): number {
        return Math.min(priceType.clientMaxQuantity, this.props.appSettingsModel.maxTicketQuantity);
    }

    getPriceTypeMinQuantity(priceType: PriceType): number {
        return priceType.clientMinQuantity;
    }

    totalPrice(price: PriceTypePrice): number {
        return price.price + price.feeAmount;
    }

    lowerTotalPrice(priceA: PriceTypePrice, priceB: PriceTypePrice): PriceTypePrice {
        if (this.totalPrice(priceA) < this.totalPrice(priceB))
            return priceA;
        else
            return priceB;
    }

    //TODO: take selected venue levels into account
    getPriceTypeFeeAmount(priceType: PriceType): string {
        const matchingPrices = flatMap(this.props.appSettingsModel?.venueLevels, a => a.priceTypePrices)?.
            filter(pt => pt != null && pt.priceTypeId === priceType.priceTypeId);

        if (matchingPrices && matchingPrices.length > 0) {
            const priceTypeCosts = matchingPrices.reduce((a, b) => this.lowerTotalPrice(a, b));

            if (this.props.appSettingsModel?.displaySeatFee && priceTypeCosts?.feeAmount) {
                // TODO: Itemize fees later if required
                const fee = priceTypeCosts.feeAmount;
                const price = priceTypeCosts.price;
                return `${!!this.props.appSettingsModel?.startingPriceFeeHeading ? `<p>${this.props.appSettingsModel?.startingPriceFeeHeading}</p>` : ''}
					<p>${this.props.appSettingsModel?.ticketCostText} ${getCurrencyFormat(this.props.appSettingsModel.currencyFormat, price)}</p>
					<p>${this.props.appSettingsModel?.feeAmountText} ${getCurrencyFormat(this.props.appSettingsModel.currencyFormat, fee)}</p>`;
            }
        }
        return '';
    }
}

export default PriceTypeFilterContainer;